import { useState, useMemo, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import isEqual from 'lodash.isequal';
import {
  ButtonSecondary,
  ButtonSize,
  Currency,
  formatBalance,
  IconExpand,
  IconTrash,
  PermissionEnum,
  Popup,
  ResponseError,
  Table,
} from '@ui/src';
import { volumesColumns } from '../variables/volumes';
import { RiskScoreRange } from '../../../store/dictionaries/types';
import { RiskScoreClientTypeEnum, Turnover } from '../../types';
import ROUTES from '../../../routes';
import { useBatchDeleteCorporateVolumesMutation } from '../../corporate-clients-risk/store/services';
import { useBatchDeleteIndividualVolumesMutation } from '../../indiviual-clients-risk/store/services';
import { VolumesListItemTypes } from './types';

import cn from '../partial.module.css';

const formatVolumesList = (
  list: Turnover[],
  riskScoreRanges?: RiskScoreRange[],
  baseCurrency?: Currency,
): Turnover[] => {
  const nextList = list?.map((item, index) => {
    const result = { ...item };

    const riskCategory = riskScoreRanges?.find(
      (riskScore) =>
        riskScore.from <= item.riskScore && riskScore.to >= item.riskScore,
    );

    const render: Record<string, React.JSX.Element> = {
      name: (
        <div className={!result?.active ? cn.inactive : ''}>
          {`${list[index + 1] ? 'From' : 'Over'} ${formatBalance(
            item.from,
            baseCurrency || ({ decimals: 0 } as Currency),
            baseCurrency ? 2 : 0,
          )} ${baseCurrency ? baseCurrency?.name : ''}${
            list[index + 1]
              ? ` to ${formatBalance(
                  item.to || list[index + 1].from,
                  baseCurrency || ({ decimals: 0 } as Currency),
                  baseCurrency ? 2 : 0,
                )} ${baseCurrency ? baseCurrency?.name : ''}`
              : ''
          }`}
        </div>
      ),
      riskScore: (
        <div className={!result?.active ? cn.inactive : ''}>
          {result.riskScore}
        </div>
      ),
      riskCategory: (
        <div
          className={!result?.active ? cn.inactive : ''}
        >{`${riskCategory?.name?.charAt(0)}${riskCategory?.name
          .slice(1)
          .toLowerCase()} risk`}</div>
      ),
    };

    result.render = render;

    return result;
  });

  return nextList;
};

const VolumesListItem = ({
  list,
  sort,
  filters,
  riskScoreRanges,
  activePermissions,
  isFetching,
  type,
  baseCurrency,
  clientType = RiskScoreClientTypeEnum.INDIVIDUAL,
  error,
}: VolumesListItemTypes) => {
  const [selectedList, setSelectedList] = useState<string[]>([]);
  const [popupVisible, setPopupVisible] = useState(false);
  const [deleteIsSuccess, setDeleteIsSuccess] = useState(false);

  const [batchDeleteIndividualVolumes, { isLoading: deleteInProgress }] =
    useBatchDeleteIndividualVolumesMutation();
  const [
    batchDeleteCorporatelVolumes,
    { isLoading: corporateDeleteInProgress },
  ] = useBatchDeleteCorporateVolumesMutation();

  const onDelete = async () => {
    setDeleteIsSuccess(false);

    if (clientType === RiskScoreClientTypeEnum.INDIVIDUAL) {
      const response = await Promise.all(
        selectedList.map(async (item) => {
          const deleteResponse = await batchDeleteIndividualVolumes(item);

          return deleteResponse as { error?: ResponseError };
        }),
      );

      if (!response.find((item) => item?.error)) {
        setDeleteIsSuccess(true);
      }
    } else {
      const response = await Promise.all(
        selectedList.map(async (item) => {
          const deleteResponse = await batchDeleteCorporatelVolumes(item);

          return deleteResponse as { error?: ResponseError };
        }),
      );

      if (!response.find((item) => item?.error)) {
        setDeleteIsSuccess(true);
      }
    }
  };

  const formattedData = useMemo(() => {
    const data = formatVolumesList(
      list as Turnover[],
      riskScoreRanges,
      baseCurrency,
    );

    return data;
  }, [list, sort, riskScoreRanges]);

  const onRowsSelect = (rows: string[]) => {
    if (!isEqual(selectedList, rows)) {
      setSelectedList(rows);
    }
  };

  useEffect(() => {
    if (deleteIsSuccess) {
      setPopupVisible(false);
    }
  }, [deleteIsSuccess]);

  return (
    <div className={cn.volumesBottom}>
      <div className={cn.volumesControls}>
        {activePermissions?.includes(
          PermissionEnum.SETTINGS_TRANSACTIONS_VOLUMES_CREATE_ACTION,
        ) && (
          <NavLink
            to={`${
              clientType === RiskScoreClientTypeEnum.INDIVIDUAL
                ? ROUTES.INDIVIDUAL_CLIENTS_RISK_VOLUMES_CREATE
                : ROUTES.CORPORATE_CLIENTS_RISK_VOLUMES_CREATE
            }?type=${type}`}
            className={cn.volumesLink}
          >
            <IconExpand />
            Add Range
          </NavLink>
        )}

        {selectedList?.length > 0 && (
          <>
            <div className={cn.separator} />
            <ButtonSecondary
              className={`button-white ${cn.volumesButton}`}
              text="Delete"
              icon={<IconTrash />}
              size={ButtonSize.SMALL}
              onClick={() => setPopupVisible(true)}
            />
          </>
        )}
      </div>
      <div className={cn.volumesTableWrapper}>
        <Table
          columns={volumesColumns}
          data={formattedData || []}
          onSort={() => {}}
          filters={filters}
          sort={sort}
          columnsLabels={{}}
          onFilterChange={() => {}}
          isLoading={isFetching}
          onRowsSelect={onRowsSelect}
          tableClassName={!list?.length ? cn.emptyTable : ''}
          tableTheadClassName={cn.tableHead}
          tableBodyClassName={`${cn.tableBody} ${
            !list?.length ? cn.tableBodyEmpty : ''
          }`}
          error={error}
          clickable
        />
      </div>
      <Popup
        title="Delete range?"
        onCancelClick={() => setPopupVisible(false)}
        onOkClick={onDelete}
        cancelButtonText="Cancel"
        okButtonText={
          deleteInProgress || corporateDeleteInProgress
            ? 'Deleting...'
            : 'Delete'
        }
        isVisible={popupVisible}
      />
    </div>
  );
};

export default VolumesListItem;
